// onboardingService — client for the Phase 49 onboarding endpoints (plan 49-08).
//
// Same shape as notificationService.ts: axios + getApiBaseUrl() + getHeaders(), the `auth_token`
// preference for the Authorization header, and every call catches into `{ ok: false, error }` so
// callers never need a try/catch. No function takes a user id or a phone: the server scopes every
// authed call by req.user (T-49-07), and the return link's signed token is its own authorisation.
//
// Backend: the WhatsApp decline marker route is plan 04; the welcome-card dismiss and the return
// link routes are plan 20 (paths are on each function below).
// Base already includes `/api` (apiClient.getApiBaseUrl), so paths never carry that prefix.

import axios from "axios";
import { getApiBaseUrl, getHeaders } from "./apiClient";
import type { BackendApiResponse } from "./adminService";

const FALLBACK_ERROR = "مقدرناش نحفظ التغيير. جرّب تاني.";

const unwrap = <T>(error: unknown): BackendApiResponse<T> => {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    return { ok: false, error: error.response.data.error };
  }
  return { ok: false, error: FALLBACK_ERROR };
};

// apiClient.getHeaders() only reads the `yetfarrag_auth` blob; email/password and Facebook logins
// write only the bare JWT to `auth_token`, so prefer it (same rule as notificationService.ts).
const authHeaders = (withAuth: boolean): Record<string, string> => {
  const headers = getHeaders(withAuth);
  if (!withAuth || typeof window === "undefined") return headers;
  try {
    const token = window.localStorage.getItem("auth_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Storage blocked (private mode): keep whatever getHeaders() found.
  }
  return headers;
};

const post = async <T>(path: string, body?: unknown, withAuth = true): Promise<BackendApiResponse<T>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${path}`, body ?? {}, { headers: authHeaders(withAuth) });
    return response.data;
  } catch (error) {
    return unwrap<T>(error);
  }
};

/** S1 dismiss X. Callers hide optimistically; a failed write is silent (the card may return). */
export const dismissWelcomeCard = async () => post<{ done: boolean }>("/users/me/onboarding/welcome-dismiss");

/** D-23 decline marker, written when a new phone user leaves the finish-screen switch off. */
export const recordWhatsappOnboardingDecline = async () =>
  post<{ done: boolean }>("/notifications/whatsapp/onboarding-decline");

/**
 * DD-12 return link. Public: sends no Authorization header, so it works signed out and never fails
 * for lack of a stored token. The token is opaque here; the server verifies it.
 */
export const postReturnToken = async (token: string) =>
  post<{ stamped: boolean }>("/onboarding/return", { token }, false);
